import React, { useContext, useEffect, useState } from "react";
import { AuthService, DynamicYieldEventService } from "@/services";
import SendOTP from "@/components/modal/modalMobileVerification/sendOtp";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  resetResponseUpdateDetailsData,
  resetTokenGeneratorCheckoutGuest,
  saveResponseUpdateDetailsData,
  setToken
} from "@/redux/actions/authConfig.action";
import {
  handleOtpUpdateMandatoryFields,
  handleOtpUpdateMandatoryFieldsAuthorize,
  resetAPIError,
  userProfile
} from "@/redux/actions/homepage.action";
import { useHistory } from "react-router-dom";
import { useCountryShort, useLanguage, useRouteSlug } from "@/hooks";
import { IS_AUTH_FROM_CHECKOUT, CHECKOUT_ID } from "@/constants";
import { changeCheckoutData } from "@/redux/actions/checkout.action";
import { setCountryListData } from "@/redux/actions/common.action";
import { SignInMode } from "../types";
import { selectGetResponseUpdateDetails } from "@/util/selectors";

type Props = {
  verificationPhone: string;
  otpToken: string;
  handleEditClick: any;
  redirection: string;
};

const OtpVerification = ({
  verificationPhone,
  otpToken,
  handleEditClick,
  redirection
}: Props) => {
  const [otpValid, setOtpValid] = useState(true);
  const [otpCode, setOtpCode] = useState("");
  const [sendOtpCount, setSendOtpCount] = useState(0);
  const [showVerifyButtonLoader, setShowVerifyButtonLoader] = useState(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState("");
  const dispatch = useAppDispatch();
  const history = useHistory();
  const language = useLanguage();
  const countrySlug = useCountryShort();
  const routeSlug = useRouteSlug();

  const onAuthCallback = response => {
    if (response?.data?.userProfile?.customerId) {
      clearOTPData();
      dispatch(resetTokenGeneratorCheckoutGuest());

      dispatch(changeCheckoutData("continueAsGuest", false));
      dispatch(changeCheckoutData("step1Completed", true));
      DynamicYieldEventService.login(response?.data?.userProfile?.email);
      if (localStorage.getItem(IS_AUTH_FROM_CHECKOUT)) {
        dispatch(setCountryListData([]));
        history.push(
          `/${routeSlug}/checkout/${localStorage.getItem(CHECKOUT_ID)}/`
        );
        localStorage.removeItem(IS_AUTH_FROM_CHECKOUT);
        return;
      }
      dispatch(resetAPIError());
    }

    if (response?.data?.userProfile?.customerId && redirection) {
      history.push(redirection);
    }
  };

  const onSendOtp = async (e, resend) => {
    setOtpCode("");
    setOtpErrorMessage("");
    setOtpValid(true);
    setSendOtpCount(prevCount => prevCount + 1);

    try {
      const response = await (resend
        ? AuthService.resendOtp({ token: otpToken })
        : AuthService.sendOtp({ token: otpToken }));
    } catch (error) {
      console.error("Error in resending OTP", error);
      setOtpErrorMessage(
        error?.response?.data?.message || "Error in resending OTP"
      );
    }
  };

  const clearOTPData = () => {
    setOtpCode("");
    setOtpErrorMessage("");
    setSendOtpCount(0);
    setOtpValid(true);
  };

  const onVerify = async () => {
    setShowVerifyButtonLoader(true);
    try {
      const resp = await dispatch(
        handleOtpUpdateMandatoryFieldsAuthorize({
          token: otpToken,
          otp: otpCode,
          history,
          language,
          country: countrySlug,
          onAuth: onAuthCallback,
          withCartRedirect: false,
          redirect: false
        })
      );
      if (!resp.success) {
        setOtpErrorMessage(resp?.message || "Invalid OTP");
        setOtpValid(false);
      }
    } finally {
      setShowVerifyButtonLoader(false);
    }
  };

  useEffect(() => {
    setOtpErrorMessage("");
    setOtpValid(true);
  }, [otpCode]);

  return (
    <SendOTP
      phone={verificationPhone}
      otp={otpCode}
      handleOTPChange={setOtpCode}
      verifyOtp={onVerify}
      otpValid={otpValid}
      sendOtp={onSendOtp}
      sendOtpCount={sendOtpCount}
      errorMessage={otpErrorMessage}
      showLoader={showVerifyButtonLoader}
      isNewSignUp
      handleEditClick={handleEditClick}
    />
  );
};

export default OtpVerification;
